import ApiService from "../../services/ApiService";
import { convertDateFormat } from "../../services/TimeService";
import translate from "../../services/Translate";

const _ERROR_MAPPINGS_STUDENT_CARD: Record<string, string> = {
  "Student card has already been generated": "PAGES.USER.USER_BLOCK.STUDENT_CARD.ERROR.ALREADY_GENERATED",
  "No profile picture found for target user.": "PAGES.USER.USER_BLOCK.STUDENT_CARD.ERROR.NO_PROFILE_PICTURE",
  "Missing birthdate.": "PAGES.USER.USER_BLOCK.STUDENT_CARD.ERROR.MISSING_BIRTHDATE",
  "Invalid birthdate format.": "PAGES.USER.USER_BLOCK.STUDENT_CARD.ERROR.INVALID_BIRTHDATE_FORMAT",
  "Birthdate cannot be in the future.": "PAGES.USER.USER_BLOCK.STUDENT_CARD.ERROR.FUTURE_BIRTHDATE",
  "Image aspect ratio must match the student card photo area":
    "PAGES.USER.USER_BLOCK.STUDENT_CARD.ERROR.INVALID_IMAGE_RATIO",
  "Invalid image file. Use JPEG or PNG.": "PAGES.USER.USER_BLOCK.STUDENT_CARD.ERROR.INVALID_IMAGE_FORMAT",
  "Invalid image dimensions.": "PAGES.USER.USER_BLOCK.STUDENT_CARD.ERROR.INVALID_IMAGE",
  "Could not read uploaded image.": "PAGES.USER.USER_BLOCK.STUDENT_CARD.ERROR.INVALID_IMAGE",
  "Image is too large.": "PAGES.USER.USER_BLOCK.STUDENT_CARD.ERROR.IMAGE_TOO_LARGE",
  "Profile picture URL is invalid.": "PAGES.USER.USER_BLOCK.STUDENT_CARD.ERROR.INVALID_IMAGE",
  "Could not load profile picture for student card validation.":
    "PAGES.USER.USER_BLOCK.STUDENT_CARD.ERROR.PROFILE_PICTURE_LOAD_ERROR",
  "Profile picture is not a valid JPEG or PNG image.": "PAGES.USER.USER_BLOCK.STUDENT_CARD.ERROR.INVALID_IMAGE_FORMAT",
  "No student card document found for regeneration.":
    "PAGES.USER.USER_BLOCK.STUDENT_CARD.ERROR.NO_DOCUMENT_TO_REGENERATE",
  "Student card document has no parameters.": "PAGES.USER.USER_BLOCK.STUDENT_CARD.ERROR.NO_DOCUMENT_PARAMETERS",
  "Error in document factory response.": "PAGES.USER.USER_BLOCK.STUDENT_CARD.ERROR.DOCUMENT_FACTORY_ERROR",
  "Error creating student card for user ": "PAGES.USER.USER_BLOCK.STUDENT_CARD.ERROR.TEMPORARY_UNAVAILABLE",
  "Error sending student card email for user ": "PAGES.USER.USER_BLOCK.STUDENT_CARD.ERROR.TEMPORARY_UNAVAILABLE",
};

const _ACADEMIC_YEAR_START_MONTH = 8;

const _getAcademicYearStart = (referenceDate: Date = new Date()): Date => {
  const year =
    referenceDate.getUTCMonth() >= _ACADEMIC_YEAR_START_MONTH
      ? referenceDate.getUTCFullYear()
      : referenceDate.getUTCFullYear() - 1;
  return new Date(Date.UTC(year, _ACADEMIC_YEAR_START_MONTH, 1));
};

export const canGenerateStudentCard = (
  hasStudentCard: boolean,
  studentCardCreatedAt: string | null | undefined,
  referenceDate: Date = new Date()
): boolean => {
  if (!hasStudentCard || !studentCardCreatedAt) {
    return true;
  }

  const createdAt = new Date(studentCardCreatedAt);
  if (Number.isNaN(createdAt.getTime())) {
    return true;
  }

  const academicYearStart = _getAcademicYearStart(referenceDate);

  return !(
    createdAt >= academicYearStart &&
    createdAt < new Date(Date.UTC(academicYearStart.getUTCFullYear() + 1, _ACADEMIC_YEAR_START_MONTH, 1))
  );
};

export const getLatestStudentCardDate = (
  documents: { type?: string; document_type?: string; dt_created?: string }[]
): string | null =>
  documents
    .filter((doc) => (doc.type === "student_card" || doc.document_type === "student_card") && doc.dt_created)
    .reduce<string | null>((latest, doc) => {
      return !latest || new Date(doc.dt_created!) > new Date(latest) ? doc.dt_created! : latest;
    }, null);

export const fetchEvaluations = (setEvaluations: Function, setLoadedCertif: Function) => {
  ApiService.get("evaluations")
    .then((response) => {
      setEvaluations(response.data.evaluations);
      setLoadedCertif(true);
    })
    .catch((error) => {
      console.error("Failed to fetch evaluations or certifications", error);
    });
};

export const refreshUserEvaluation = (
  user: any,
  evaluations: any[],
  setEvaluations: Function,
  refreshUserCertifications: Function
) => {
  try {
    const userCertificationsNames = new Set(user.user_certifs.map((cert: any) => cert.certification_name));
    const availableEvaluations = evaluations.filter((evaluation: any) => !userCertificationsNames.has(evaluation.name));
    setEvaluations(availableEvaluations);
    refreshUserCertifications();
  } catch (error) {
    console.error("Failed to fetch certifications", error);
  }
};

export const handleSaveEvaluations = (
  user: any,
  selectedEvaluations: any[],
  setButtonClicked: Function,
  evaluations: any[],
  setEvaluations: Function,
  setShowEvaluationsModal: Function,
  setSelectedEvaluations: Function,
  refreshUserEvaluation: Function,
  refreshUserCertifications: Function,
  language: any
) => {
  if (selectedEvaluations.length > 0 && user) {
    setButtonClicked(true);
    const postPromises = selectedEvaluations.map((evaluation) =>
      ApiService.post(`users/${user.id}/certifications`, {
        evaluation_id: evaluation.id,
      })
    );

    Promise.allSettled(postPromises)
      .then((responses) => {
        const failedRequests = responses.filter((response) => response.status === "rejected");
        if (failedRequests.length > 0) {
          console.error(translate(language, "PAGES.USER.USER_BLOCK.ERROR_CERTIF_ALREADY_GIVEN"), failedRequests);
          alert(translate(language, "PAGES.USER.USER_BLOCK.ERROR_CERTIF_ALREADY_GIVEN"));
        } else {
          alert(translate(language, "PAGES.USER.USER_BLOCK.CERTIF_SUCCESS_SAVE"));
        }
        setSelectedEvaluations([]);
        setShowEvaluationsModal(false);
        refreshUserEvaluation(user, evaluations, setEvaluations, refreshUserCertifications);
        setButtonClicked(false);
      })
      .catch((error) => {
        console.error(translate(language, "PAGES.USER.USER_BLOCK.ERROR_OTHER"));
        alert(translate(language, "PAGES.USER.USER_BLOCK.ERROR_OTHER"));
        setButtonClicked(false);
      });
  } else {
    console.error(translate(language, "PAGES.USER.USER_BLOCK.ERROR_OTHER"));
    setButtonClicked(false);
  }
};

export const fetchProgressReport = (
  language: string,
  userId: number,
  userName: { firstName: string; lastName: string },
  params: {
    cohortId?: number;
    cursusName?: string;
    sections?: string[];
    logTimeFormats?: string[];
    includeMeetingsLogs?: boolean;
    includeModulesEstimatedTime?: boolean;
  },
  setLoadingReport: Function,
  successCallBack: Function,
  isExternal: boolean
) => {
  if (!params.cohortId) {
    alert(translate(language, "PAGES.USER.USER_BLOCK.REPORT.ERROR_NEED_OPTIONS"));
    return;
  }

  if (!isExternal && !params.sections?.length) {
    alert(translate(language, "PAGES.USER.USER_BLOCK.REPORT.ERROR_NEED_OPTIONS"));
    return;
  }

  setLoadingReport(true);

  const request = isExternal
    ? ApiService.postDownload(`users/${userId}/${params.cohortId}/progress/external-format`, {})
    : ApiService.postDownload(`users/${userId}/${params.cohortId}/progress`, {
        cursus_name: params.cursusName ?? "",
        sections: params.sections,
        log_time_formats: params.logTimeFormats ?? [],
        include_meetings_logs: params.includeMeetingsLogs ?? false,
        get_module_estimated_time: params.includeModulesEstimatedTime ?? false,
      });

  request
    .then((resp) => {
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      const learnerName = [userName.firstName, userName.lastName]
        .join("_")
        .replace(/[<>:"/\\|?*]/g, "")
        .replace(/\s+/g, "_");
      link.href = url;
      link.setAttribute("download", `${learnerName}_progress_report${isExternal ? "_external" : "_internal"}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      successCallBack();
    })
    .catch((err) => {
      console.error(err);
      alert(translate(language, "ERROR.API_ERROR"));
    })
    .finally(() => setLoadingReport(false));
};

export const inviteUserToEvaluation = (
  userId: number,
  evaluationId: number,
  language: string,
  refreshUserExams: Function,
  scheduledDate?: string
) => {
  ApiService.post(`users/${userId}/evaluations/${evaluationId}/attempts`, {
    send_email: true,
    ...(scheduledDate && { dt_start: new Date(scheduledDate).toISOString() }),
  })
    .then((_) => {
      alert(translate(language, "PAGES.USER.USER_BLOCK.INVITE_EVAL.EVAL_SUCCESS_INVITATION"));
      refreshUserExams();
    })
    .catch((error) => {
      if (error.data?.error?.message.includes("must be a future date and time")) {
        console.error("A past date error occurred:", error);
        alert(translate(language, "PAGES.USER.USER_BLOCK.INVITE_EVAL.PAST_DATE_ERROR"));
      } else {
        console.error("An API error occurred:", error);
        alert(translate(language, "ERROR.API_ERROR"));
      }
    });
};

export const mergeUser = (
  userIdToDelete: number,
  userIdToKeep: number,
  language: string,
  successCallBack: Function
) => {
  ApiService.post("users/merge", {
    user_id_to_delete: userIdToDelete,
    user_id_to_keep: userIdToKeep,
  })
    .then(() => {
      alert(translate(language, "PAGES.USER.USER_BLOCK.MERGE.SUCCESS"));
      successCallBack();
    })
    .catch((error) => {
      console.error("Merge error:", error);
      alert(translate(language, "PAGES.USER.USER_BLOCK.MERGE.ERROR"));
    });
};

export const resendCredentials = (userId: number, language: string, setLoading: Function, onSuccess?: Function) => {
  setLoading(true);
  ApiService.post(`users/${userId}/resend-credentials`, {})
    .then(() => onSuccess?.())
    .catch((error) => {
      console.error("Resend credentials error:", error);
      alert(
        translate(
          language,
          error?.data?.error?.message?.includes("Error sending credentials email")
            ? "PAGES.USER.USER_BLOCK.RESEND_CREDENTIALS_ERROR"
            : "ERROR.API_ERROR"
        )
      );
    })
    .finally(() => setLoading(false));
};

export const generateStudentCard = (
  language: string,
  userId: number,
  birthdate: string,
  image: File | null,
  setLoading: Function,
  onSuccess?: Function
) => {
  if (!birthdate) {
    alert(translate(language, "PAGES.USER.USER_BLOCK.STUDENT_CARD.ALERT.BIRTHDATE"));
    return;
  }

  setLoading(true);

  const formData = new FormData();
  formData.append("birthdate", convertDateFormat(birthdate.replace(/-/g, "/")));
  if (image) {
    formData.append("image", image);
  }

  ApiService.post(`users/${userId}/documents/student-cards`, formData, { "Content-Type": "multipart/form-data" })
    .then(() => onSuccess?.())
    .catch((error) => {
      const apiMessage = error?.data?.error?.message;

      if (typeof apiMessage !== "string" || !apiMessage.length) {
        alert(translate(language, "ERROR.API_ERROR"));
        return;
      }

      const matchedKey = Object.keys(_ERROR_MAPPINGS_STUDENT_CARD).find((key) => apiMessage.includes(key));

      const translationKey = matchedKey ? _ERROR_MAPPINGS_STUDENT_CARD[matchedKey] : "ERROR.API_ERROR";

      alert(translate(language, translationKey));
    })
    .finally(() => setLoading(false));
};

export const regenerateStudentCard = (
  language: string,
  userId: number,
  birthdate: string,
  image: File | null,
  originPreviousImage: boolean,
  setLoading: Function,
  onSuccess?: Function
) => {
  if (!birthdate) {
    alert(translate(language, "PAGES.USER.USER_BLOCK.STUDENT_CARD.ALERT.BIRTHDATE"));
    return;
  }

  setLoading(true);

  const formattedBirthdate = convertDateFormat(birthdate.replace(/-/g, "/"));
  const request = image
    ? (() => {
        const formData = new FormData();
        formData.append("birthdate", formattedBirthdate);
        formData.append("image", image);
        return ApiService.post(`users/${userId}/documents/regenerate/student-cards`, formData, {
          "Content-Type": "multipart/form-data",
        });
      })()
    : ApiService.post(
        `users/${userId}/documents/regenerate/student-cards`,
        {
          birthdate: formattedBirthdate,
          ...(originPreviousImage ? { previous_image: true } : {}),
        },
        { "Content-Type": "application/json" }
      );

  request
    .then(() => onSuccess?.())
    .catch((error) => {
      const apiMessage = error?.data?.error?.message;

      if (typeof apiMessage !== "string" || !apiMessage.length) {
        alert(translate(language, "ERROR.API_ERROR"));
        return;
      }

      const matchedKey = Object.keys(_ERROR_MAPPINGS_STUDENT_CARD).find((key) => apiMessage.includes(key));

      const translationKey = matchedKey ? _ERROR_MAPPINGS_STUDENT_CARD[matchedKey] : "ERROR.API_ERROR";

      alert(translate(language, translationKey));
    })
    .finally(() => setLoading(false));
};
